// src/pages/Login.tsx
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Eye, EyeOff } from "lucide-react";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const { toast } = useToast();

  // This effect runs when the component loads
  useEffect(() => {
    const loginError = sessionStorage.getItem('loginError');
    if (loginError) {
      setError(loginError);
      sessionStorage.removeItem('loginError'); // Clear it after displaying
    }
  }, []);

  // Clear error when user starts typing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.id === 'email') {
      setEmail(e.target.value);
    } else if (e.target.id === 'password') {
      setPassword(e.target.value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    if (!email.trim() || !password.trim()) {
      const message = "Please enter both email and password";
      setError(message);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      await login(email, password);
      toast({
        variant: "default",
        title: "Success",
        description: "Successfully logged in"
      });
      setIsLoading(false); // Set loading to false on success
    } catch (error: any) {
      let errorMessage = error.message || "An error occurred during login";
      if (
        error?.response?.status === 401 ||
        errorMessage.toLowerCase().includes("unauthorized")
      ) {
        errorMessage = "Invalid Email ID or Password. Please try again with the correct credentials.";
      }
      // Save error to sessionStorage before setting state
      sessionStorage.setItem('loginError', errorMessage);
      setError(errorMessage);
      setPassword('');
      setIsLoading(false); // Set loading to false on error
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 p-4">
      {/* Show error alert above the card for visibility */}
      {error && (
        <div className="fixed top-4 left-1/2 z-50 w-full max-w-md -translate-x-1/2 transform">
          <Alert variant="destructive" className="mb-4 flex items-center justify-between">
            <AlertDescription>{error}</AlertDescription>
            <button
              onClick={() => setError(null)}
              className="ml-4 rounded bg-red-100 px-2 py-1 text-red-700 hover:bg-red-200"
              aria-label="Close"
              type="button"
            >
              Close
            </button>
          </Alert>
        </div>
      )}
      <Card className="w-full max-w-md overflow-hidden shadow-lg">
        <div
          className="flex flex-col items-center justify-center px-6 py-8"
          style={{
            background: "linear-gradient(145deg, #1a304e 0%, #243c5a 55%, #1e3554 100%)",
          }}
        >
          <img
            src="/logo.png"
            alt="Navsan Logo"
            className="h-16 w-auto max-w-[240px] object-contain"
          />
        </div>
        <CardHeader className="pt-6">
          <CardTitle className="text-center">Navsan's Timesheet Portal</CardTitle>
          <CardDescription className="text-center">
            Please sign in to access your weekly timesheet
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={handleInputChange}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            <Button 
              type="submit" 
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner" /> Signing in...
                </>
              ) : "Sign In"}
            </Button>
          </form>
          <Alert className="mt-4 px-3 py-2">
            <AlertDescription className="whitespace-nowrap text-center text-xs leading-tight">
              Use only the default password provided by your admin.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
      {/* Spinner overlay */}
      {isLoading && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(255,255,255,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999
          }}
        >
          <div
            className="spinner"
            style={{
              border: "8px solid #f3f3f3",
              borderTop: "8px solid #3498db",
              borderRadius: "50%",
              width: 60,
              height: 60,
              animation: "spin 1s linear infinite"
            }}
          />
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg);}
                100% { transform: rotate(360deg);}
              }
            `}
          </style>
        </div>
      )}
    </div>
  );
};

export default Login;